import React, { useState, useEffect } from 'react';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Plus, Trophy, TrendingUp, Radio, Award, Heart, Video, Trash2, FileText, ExternalLink, AlertCircle, Upload, Image as ImageIcon } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import DeleteConfirmDialog from '../components/DeleteConfirmDialog';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Highlights = ({ user, selectedArtist }) => {
  const [highlights, setHighlights] = useState([]);
  const [open, setOpen] = useState(false);
  const [importDialogOpen, setImportDialogOpen] = useState(false);
  const [importing, setImporting] = useState(false);
  const [selectedImage, setSelectedImage] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [highlightToDelete, setHighlightToDelete] = useState(null);
  const [highlightFormData, setHighlightFormData] = useState({
    title: '',
    category: 'press',
    description: '',
    date: new Date().toISOString().split('T')[0],
    metric_value: '',
    link: '',
  });

  const artistId = user?.role === 'artist' ? user.id : selectedArtist;

  useEffect(() => {
    if (artistId && artistId !== 'all') {
      fetchHighlights();
    }
  }, [artistId]);

  const fetchHighlights = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/highlights?artist_id=${artistId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setHighlights(response.data);
    } catch (error) { // Silent fail
      
    }
  };

  const handleAddHighlight = async (e) => {
    e.preventDefault();
    
    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${API}/highlights`,
        {
          ...highlightFormData,
          artist_id: artistId,
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Highlight added successfully!');
      setOpen(false);
      setHighlightFormData({
        title: '',
        category: 'press',
        description: '',
        date: new Date().toISOString().split('T')[0],
        metric_value: '',
        link: '',
      });
      fetchHighlights();
    } catch (error) { // Silent fail
      toast.error(error.response?.data?.detail || 'Failed to add highlight');
    }
  };

  const confirmDeleteHighlight = (highlight) => {
    setHighlightToDelete(highlight);
    setDeleteDialogOpen(true);
  };

  const handleDeleteHighlight = async () => {
    if (!highlightToDelete) return;

    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/highlights/${highlightToDelete.id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success('Highlight deleted!');
      setDeleteDialogOpen(false);
      setHighlightToDelete(null);
      fetchHighlights();
    } catch (error) { // Silent fail
      toast.error('Failed to delete highlight');
    }
  };

  const handleImageSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 10 * 1024 * 1024) {
        toast.error('Image size should be less than 10MB');
        return;
      }
      setSelectedImage(file);
    }
  };

  const handleImportFromImage = async () => {
    if (!selectedImage) {
      toast.error('Please select an image first');
      return;
    }

    if (!artistId || artistId === 'all') {
      toast.error('Please select a specific artist before importing');
      return;
    }

    setImporting(true);
    try {
      const reader = new FileReader();
      reader.onload = async (event) => {
        const base64Data = event.target.result;
        const token = localStorage.getItem('token');
        
        const response = await axios.post(
          `${API}/highlights/import-from-image`,
          {
            image_data: base64Data,
            artist_id: artistId
          },
          { headers: { Authorization: `Bearer ${token}` } }
        );
        
        toast.success(response.data.message || 'Highlights imported successfully!');
        setImportDialogOpen(false);
        setSelectedImage(null);
        await fetchHighlights();
      };
      reader.readAsDataURL(selectedImage);
    } catch (error) { // Silent fail
      toast.error(error.response?.data?.detail || 'Failed to import from image');
    } finally {
      setImporting(false);
    }
  };

  const getCategoryIcon = (category) => {
    switch (category) {
      case 'streaming': return <TrendingUp className="w-4 h-4" />;
      case 'press': return <Radio className="w-4 h-4" />;
      case 'commercial': return <Trophy className="w-4 h-4" />;
      case 'social': return <Heart className="w-4 h-4" />;
      case 'industry': return <Award className="w-4 h-4" />;
      case 'creative': return <Video className="w-4 h-4" />;
      default: return <Trophy className="w-4 h-4" />;
    }
  };

  const getCategoryColor = (category) => {
    switch (category) {
      case 'streaming': return 'bg-green-500/20 text-green-500 border-green-500/30';
      case 'press': return 'bg-blue-500/20 text-blue-500 border-blue-500/30';
      case 'commercial': return 'bg-yellow-500/20 text-yellow-500 border-yellow-500/30';
      case 'social': return 'bg-pink-500/20 text-pink-500 border-pink-500/30';
      case 'industry': return 'bg-purple-500/20 text-purple-500 border-purple-500/30';
      case 'creative': return 'bg-orange-500/20 text-orange-500 border-orange-500/30';
      default: return 'bg-gray-500/20 text-gray-500 border-gray-500/30';
    }
  };

  const generateHighlightSheet = () => {
    if (highlights.length === 0) {
      toast.error('No highlights to generate sheet from');
      return;
    }

    // Group highlights by category
    const grouped = highlights.reduce((acc, highlight) => {
      if (!acc[highlight.category]) acc[highlight.category] = [];
      acc[highlight.category].push(highlight);
      return acc;
    }, {});

    // Generate text
    let sheet = `🌟 Artist Highlights & Achievements\n`;
    sheet += `Generated: ${new Date().toLocaleDateString()}\n`;
    sheet += `\n━━━━━━━━━━━━━━━━━━━━━━━━━━━\n\n`;

    const categoryNames = {
      streaming: '📊 STREAMING & PERFORMANCE',
      press: '📰 PRESS & MEDIA',
      commercial: '💰 COMMERCIAL SUCCESS',
      social: '❤️ SOCIAL ENGAGEMENT',
      industry: '🏆 INDUSTRY RECOGNITION',
      creative: '🎬 CREATIVE MILESTONES'
    };

    Object.entries(grouped).forEach(([category, categoryHighlights]) => {
      sheet += `${categoryNames[category] || category.toUpperCase()}\n`;
      categoryHighlights
        .sort((a, b) => new Date(b.date) - new Date(a.date))
        .forEach(highlight => {
          sheet += `• ${highlight.title}`;
          if (highlight.metric_value) sheet += ` (${highlight.metric_value})`;
          sheet += ` - ${new Date(highlight.date).toLocaleDateString()}`;
          if (highlight.description) sheet += `\n  ${highlight.description}`;
          if (highlight.link) sheet += `\n  🔗 ${highlight.link}`;
          sheet += `\n`;
        });
      sheet += `\n`;
    });

    sheet += `\nTotal Highlights: ${highlights.length}`;

    // Copy to clipboard
    navigator.clipboard.writeText(sheet);
    toast.success('Highlight sheet copied to clipboard!');
  };

  if (!artistId || artistId === 'all') {
    return (
      <div className="space-y-8">
        <Card className="bg-card border-border/50 p-12 text-center">
          <AlertCircle className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
          <p className="text-muted-foreground">Please select an artist to view their highlights.</p>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-manrope font-extrabold text-3xl md:text-4xl text-foreground">
            {user?.role === 'artist' ? 'My Highlights' : 'Artist Highlights'}
          </h1>
          <p className="text-muted-foreground text-base mt-2">
            Track ongoing wins, achievements, and press coverage
          </p>
        </div>
        <div className="flex gap-3">
          {highlights.length > 0 && (
            <Button
              variant="outline"
              onClick={generateHighlightSheet}
            >
              <FileText className="w-4 h-4 mr-2" />
              Generate Sheet
            </Button>
          )}
          <Dialog open={importDialogOpen} onOpenChange={setImportDialogOpen}>
            <DialogTrigger asChild>
              <Button variant="outline">
                <ImageIcon className="w-4 h-4 mr-2" />
                Import from Image
              </Button>
            </DialogTrigger>
            <DialogContent className="bg-card border-border/50">
              <DialogHeader>
                <DialogTitle className="font-manrope font-bold text-2xl">Import Highlights from Image</DialogTitle>
              </DialogHeader>
              <div className="space-y-4">
                <div className="p-4 rounded-xl bg-accent/20 border border-border/50">
                  <p className="text-sm text-muted-foreground mb-2">
                    Upload a screenshot or image of press coverage, radio play, or achievements and AI will automatically extract:
                  </p>
                  <ul className="text-sm text-muted-foreground space-y-1 list-disc list-inside">
                    <li>Title and description</li>
                    <li>Date (if visible)</li>
                    <li>Category (press, streaming, etc.)</li>
                    <li>Links or metrics</li>
                  </ul>
                </div>

                {!artistId || artistId === 'all' ? (
                  <div className="p-4 rounded-xl bg-yellow-500/10 border border-yellow-500/30">
                    <p className="text-sm text-yellow-600 dark:text-yellow-400">
                      ⚠️ Please select a specific artist from the dropdown above before importing
                    </p>
                  </div>
                ) : null}

                <div className="space-y-2">
                  <Label htmlFor="highlight-image">Select Image</Label>
                  <Input
                    id="highlight-image"
                    type="file"
                    accept="image/*"
                    onChange={handleImageSelect}
                    className="bg-input/50 border-border rounded-xl"
                  />
                  {selectedImage && (
                    <p className="text-sm text-green-600 flex items-center gap-2">
                      ✓ Selected: {selectedImage.name} ({(selectedImage.size / 1024).toFixed(1)} KB)
                    </p>
                  )}
                </div>

                <Button
                  onClick={handleImportFromImage}
                  disabled={importing || !selectedImage || !artistId || artistId === 'all'}
                  className="w-full bg-primary hover:bg-primary/90"
                >
                  <Upload className="w-4 h-4 mr-2" />
                  {importing ? 'Importing...' : 'Import Highlights'}
                </Button>
              </div>
            </DialogContent>
          </Dialog>
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button className="bg-primary text-primary-foreground hover:bg-primary/90 shadow-[0_0_20px_rgba(124,58,237,0.3)]">
                <Plus className="w-4 h-4 mr-2" />
                Add Highlight
              </Button>
            </DialogTrigger>
            <DialogContent className="bg-card border-border/50 max-h-[90vh]">
              <DialogHeader>
                <DialogTitle className="font-manrope font-bold text-2xl">Add New Highlight</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleAddHighlight} className="space-y-4 overflow-y-auto max-h-[70vh]">
                <div className="space-y-2">
                  <Label htmlFor="highlight-category">Category</Label>
                  <select
                    id="highlight-category"
                    value={highlightFormData.category}
                    onChange={(e) => setHighlightFormData({ ...highlightFormData, category: e.target.value })}
                    className="w-full px-4 py-2 bg-input/50 border border-border text-foreground rounded-xl focus:border-primary focus:ring-2 focus:ring-primary/20 outline-none"
                    required
                  >
                    <option value="press">📰 Press & Media Coverage</option>
                    <option value="streaming">📊 Streaming & Performance Metrics</option>
                    <option value="commercial">💰 Commercial Success</option>
                    <option value="social">❤️ Social Engagement</option>
                    <option value="industry">🏆 Industry Recognition</option>
                    <option value="creative">🎬 Creative Milestones</option>
                  </select>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="highlight-title">Title *</Label>
                  <Input
                    id="highlight-title"
                    placeholder="e.g., BBC Introducing Sheffield Mix"
                    value={highlightFormData.title}
                    onChange={(e) => setHighlightFormData({ ...highlightFormData, title: e.target.value })}
                    required
                    className="bg-input/50 border-border rounded-xl"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="highlight-link">Link (optional)</Label>
                  <Input
                    id="highlight-link"
                    type="url"
                    placeholder="https://www.bbc.co.uk/programmes/..."
                    value={highlightFormData.link}
                    onChange={(e) => setHighlightFormData({ ...highlightFormData, link: e.target.value })}
                    className="bg-input/50 border-border rounded-xl"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="highlight-metric">Metric/Number (optional)</Label>
                  <Input
                    id="highlight-metric"
                    placeholder="e.g., 2M listeners, #42 on charts"
                    value={highlightFormData.metric_value}
                    onChange={(e) => setHighlightFormData({ ...highlightFormData, metric_value: e.target.value })}
                    className="bg-input/50 border-border rounded-xl"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="highlight-description">Description (optional)</Label>
                  <Textarea
                    id="highlight-description"
                    placeholder="Additional context or details..."
                    value={highlightFormData.description}
                    onChange={(e) => setHighlightFormData({ ...highlightFormData, description: e.target.value })}
                    className="bg-input/50 border-border rounded-xl min-h-20"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="highlight-date">Date</Label>
                  <Input
                    id="highlight-date"
                    type="date"
                    value={highlightFormData.date}
                    onChange={(e) => setHighlightFormData({ ...highlightFormData, date: e.target.value })}
                    required
                    className="bg-input/50 border-border rounded-xl"
                  />
                </div>

                <div className="flex gap-2">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => {
                      setOpen(false);
                      setHighlightFormData({
                        title: '',
                        category: 'press',
                        description: '',
                        date: new Date().toISOString().split('T')[0],
                        metric_value: '',
                        link: '',
                      });
                    }}
                    className="flex-1"
                  >
                    Cancel
                  </Button>
                  <Button
                    type="submit"
                    className="flex-1 bg-primary hover:bg-primary/90"
                  >
                    Add Highlight
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {highlights.length === 0 ? (
        <Card className="bg-card border-border/50 p-12 text-center">
          <Trophy className="w-16 h-16 text-muted-foreground mx-auto mb-4 opacity-50" />
          <p className="text-muted-foreground text-lg mb-2">No highlights yet</p>
          <p className="text-muted-foreground text-sm">Start tracking your achievements and press coverage!</p>
        </Card>
      ) : (
        <div className="grid gap-4">
          {highlights
            .sort((a, b) => new Date(b.date) - new Date(a.date))
            .map((highlight) => (
              <Card
                key={highlight.id}
                className={`p-6 border-2 ${getCategoryColor(highlight.category)}`}
              >
                <div className="flex items-start gap-4">
                  <div className="mt-1">
                    {getCategoryIcon(highlight.category)}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex-1">
                        <h3 className="text-lg font-semibold mb-1">{highlight.title}</h3>
                        {highlight.metric_value && (
                          <p className="text-sm font-mono opacity-80 mb-2">{highlight.metric_value}</p>
                        )}
                        {highlight.description && (
                          <p className="text-sm opacity-70 mb-2">{highlight.description}</p>
                        )}
                        {highlight.link && (
                          <a
                            href={highlight.link}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-sm text-primary hover:underline flex items-center gap-1 mb-2"
                          >
                            <ExternalLink className="w-3 h-3" />
                            View Link
                          </a>
                        )}
                        <p className="text-xs opacity-60 mt-2">
                          {new Date(highlight.date).toLocaleDateString('en-US', {
                            weekday: 'long',
                            year: 'numeric',
                            month: 'long',
                            day: 'numeric'
                          })}
                        </p>
                      </div>
                      {user?.role === 'manager' || user?.role === 'admin' && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => confirmDeleteHighlight(highlight)}
                          className="h-8 w-8 p-0 opacity-60 hover:opacity-100"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      )}
                    </div>
                  </div>
                </div>
              </Card>
            ))}
        </div>
      )}

      {/* Delete Confirmation Dialog */}
      <DeleteConfirmDialog
        open={deleteDialogOpen}
        onOpenChange={(open) => {
          setDeleteDialogOpen(open);
          if (!open) setHighlightToDelete(null);
        }}
        onConfirm={handleDeleteHighlight}
        title="Delete Highlight"
        itemName={highlightToDelete?.title}
        description="Are you sure you want to delete this highlight? This action cannot be undone."
      />
    </div>
  );
};

export default Highlights;
